/**
 * OpenAI Service for converting natural language to mathematical expressions
 */
import FunctionPlaygroundOpenAiWrapper from './FunctionPlaygroundOpenAiWrapper';

// Define the response type from OpenAI
interface OpenAIResponse {
  expression: string;
  explanation: string;
}

/**
 * Converts natural language description to a mathematical expression using OpenAI
 * @param description The natural language description of the function
 * @param apiKey Optional API key to use for the request
 * @returns Promise with the generated expression and explanation
 */
// Lazy singleton wrapper aligned with AI Toolbox
let _wrapper: FunctionPlaygroundOpenAiWrapper | null = null;
const getWrapper = () => {
  if (!_wrapper) {
    _wrapper = new FunctionPlaygroundOpenAiWrapper();
  }
  return _wrapper;
};

export const convertNaturalLanguageToExpression = async (
  description: string,
  _apiKey?: string | null
): Promise<OpenAIResponse> => {
  try {
    // Determine current UI language for localized explanation text
    const getCurrentLanguage = (): string => {
      try {
        const stored = localStorage.getItem('lang');
        if (stored && ['en', 'de', 'es', 'fr'].includes(stored)) return stored;
      } catch (_e) { /* noop */ }
      // Fallbacks
      if (typeof navigator !== 'undefined' && navigator.language) {
        const code = navigator.language.split('-')[0];
        if (['en', 'de', 'es', 'fr'].includes(code)) return code;
      }
      return 'en';
    };
    const language = getCurrentLanguage();

    // Use shared OpenAI wrapper pattern (fetch ephemeral key + call baseURL)
    const wrapper = getWrapper();
    const content = await wrapper.executeWithRetry(async (client) => {
      const completion = await client.chat.completions.create({
        model: 'gpt-4.1-mini',
        messages: [
          {
            role: 'system',
            content:
              'You are a mathematical expression generator. Convert natural language descriptions OR LaTeX expressions into JavaScript mathematical expressions.\n' +
              'Follow these rules:\n' +
              "1. Return only valid JavaScript expressions that can be evaluated\n" +
              "2. Use 'x' as the variable\n" +
              '3. Use Math.sin, Math.cos, Math.tan, Math.sqrt, Math.abs, Math.log, Math.exp, etc. for mathematical functions\n' +
              '4. If the input is LaTeX (e.g., delimited with $, $$, \\(...\\), \\[...\\], or using \\frac, \\sqrt, ^), interpret it and convert to an equivalent JavaScript expression. Examples: \\frac{x}{2} -> (x)/(2), \\sqrt{x} -> sqrt(x), |x| -> abs(x).\n' +
              `5. Format your response as a JSON object: {"expression": <JS expression>, "explanation": <one sentence in ${language}>}\n` +
              '6. Do not include any other text in your response, only the JSON object',
          },
          {
            role: 'user',
            content: `Convert this description to a JavaScript mathematical expression. Also provide a one-sentence explanation in locale '${language}': ${description}`,
          },
        ],
        temperature: 0.2,
        max_tokens: 300,
      });
      return completion.choices?.[0]?.message?.content || '';
    });
    try {
      const parsed = JSON.parse(content);
      return {
        expression: parsed.expression,
        explanation: parsed.explanation
      };
    } catch {
      const expressionMatch = content.match(/expression["\s:]+([^"]+)/i);
      const explanationMatch = content.match(/explanation["\s:]+([^"]+)/i);
      return {
        expression: expressionMatch ? expressionMatch[1].replace(/[",}]/g, '').trim() : 'x',
        explanation: explanationMatch ? explanationMatch[1].replace(/[",}]/g, '').trim() : 'Generated using OpenAI'
      };
    }
  } catch (error) {
    console.error('Error converting natural language to expression:', error);
    throw new Error('Failed to convert natural language to expression');
  }
};
